/**
 * @file inward-pending-actions.tsx
 * @module app/modules/inward/components/inward-pending-actions
 * @description Dashboard "Pending actions" card (currently UProcure Inward actions; built so
 *   other modules' pending actions can be added later). Shows the actions the
 *   logged-in user can perform in the selected warehouse (every role's actions for a super
 *   admin), each with
 *   its SLA: due in / overdue by, kept live. Tapping an item opens the GRN or PO.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import APICallService from "../../../../api/apiCallService";
import { INWARD } from "../../../../api/apiEndPoints";
import { PermissionModule } from "../../../../utils/constants";
import { useWarehouse } from "../../../context/WarehouseContext";
import { useAuth } from "../../auth";
import { canPerformInwardAction, InwardAction } from "../utils/inward-access";
import {
  ACTION_TITLES,
  dueText,
  InwardSlaAction,
  PendingActionItem,
  PendingActionsResponse,
} from "../utils/inward-sla";
import { formatDateTime } from "../utils/inward-timeline";
import "../styles/inward.css";

const BASE = "/UProcure/inward";
const REFRESH_MS = 60_000;
const TICK_MS = 30_000;
const MAX_SHOWN = 8;

const InwardPendingActions = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const canView = canPerformInwardAction(currentUser, InwardAction.View);
  // A super admin sees every warehouse; everyone else the selected one
  // Always the selected warehouse. A super admin sees every role's actions there; others
  // only the actions their access allows.
  const warehouseId = selectedWarehouse?._id;
  const [data, setData] = useState<PendingActionsResponse | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const response = await new APICallService(
      INWARD.pendingActions,
      {},
      "",
      "",
      "",
      "",
      PermissionModule.Inward,
      warehouseId
    ).callAPI();
    if (response?.items) {
      setData(response);
      setNow(new Date());
    }
  }, [warehouseId]);

  useEffect(() => {
    if (!canView) return undefined;
    load();
    const refresh = window.setInterval(load, REFRESH_MS);
    const tick = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => {
      window.clearInterval(refresh);
      window.clearInterval(tick);
    };
  }, [canView, load]);

  // Always a card: other modules' pending actions will be added here later
  if (!canView || !data) {
    return (
      <Card
        className="h-100 inward-pending-card"
        data-testid="inward-pending-actions"
      >
        <Card.Header className="border-bottom align-items-center">
          <div className="card-title">
            <h2 className="fs-22 fw-bolder mb-0">Pending actions</h2>
          </div>
        </Card.Header>
        <Card.Body className="p-6 fs-15 text-muted" data-testid="pending-empty">
          {canView ? "Loading…" : "Nothing pending."}
        </Card.Body>
      </Card>
    );
  }

  const open = (item: PendingActionItem) => {
    if (item.grnId) {
      navigate(`${BASE}/grn`, { state: { grnId: item.grnId } });
    } else if (item.action === "startInward" && item.poId) {
      navigate(`${BASE}/create-grn`, { state: { poId: item.poId } });
    } else {
      navigate(BASE);
    }
  };

  const items = showAll ? data.items : data.items.slice(0, MAX_SHOWN);
  const overdueNow = data.items.filter((i) => dueText(i, now).overdue).length;

  return (
    <Card
      className="h-100 inward-pending-card"
      data-testid="inward-pending-actions"
    >
      <Card.Header className="border-bottom align-items-center">
        <div className="card-title flex-column">
          <h2 className="fs-22 fw-bolder mb-0">Pending actions</h2>
          <span className="fs-13 text-muted">
            {data.superAdmin
              ? `All roles · ${
                  selectedWarehouse?.title ||
                  selectedWarehouse?.label ||
                  "selected warehouse"
                }`
              : `Actions waiting for you · ${
                  selectedWarehouse?.title ||
                  selectedWarehouse?.label ||
                  "selected warehouse"
                }`}
          </span>
        </div>
        <div className="d-flex gap-2">
          <Badge
            bg=""
            className="inward-badge inward-badge--neutral fs-13"
            data-testid="pending-total"
          >
            {data.items.length} pending
          </Badge>
          <Badge
            bg=""
            className={`inward-badge fs-13 ${
              overdueNow ? "inward-badge--danger" : "inward-badge--success"
            }`}
            data-testid="pending-overdue"
          >
            {overdueNow} overdue
          </Badge>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {data.items.length === 0 ? (
          <div className="p-6 fs-15 text-muted" data-testid="pending-empty">
            Nothing pending. Everything is within SLA.
          </div>
        ) : (
          <ul className="inward-pending-list" data-testid="pending-list">
            {items.map((item) => {
              const due = dueText(item, now);
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={`inward-pending-item ${
                      due.overdue ? "inward-pending-item--overdue" : ""
                    }`}
                    onClick={() => open(item)}
                    data-testid={`pending-${item.key}`}
                  >
                    <span className="d-flex justify-content-between gap-2 align-items-start">
                      <span className="fs-15 fw-bold text-start">
                        {item.title}
                      </span>
                      <span
                        className={`inward-badge fs-12 ${
                          due.overdue
                            ? "inward-badge--danger"
                            : "inward-badge--warning"
                        }`}
                        data-testid="pending-due"
                      >
                        {due.overdue
                          ? `OVERDUE · ${due.text.replace("Overdue by ", "")}`
                          : due.text}
                      </span>
                    </span>
                    <span className="fs-13 text-muted text-start d-block">
                      {item.detail}
                      {item.warehouseName ? ` · ${item.warehouseName}` : ""}
                    </span>
                    <span className="fs-13 text-start d-block">
                      {item.pendingWith} · since {formatDateTime(item.since)} ·
                      SLA {item.slaMinutes} min
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        <div
          className="d-flex flex-wrap gap-2 px-5 py-3 border-top fs-13"
          data-testid="pending-by-action"
        >
          {(Object.keys(ACTION_TITLES) as InwardSlaAction[])
            .filter((k) => data.summary.byAction[k]?.total)
            .map((k) => (
              <span key={k}>
                {ACTION_TITLES[k]}:{" "}
                <strong>{data.summary.byAction[k].total}</strong>
                {data.summary.byAction[k].overdue
                  ? ` (${data.summary.byAction[k].overdue} overdue)`
                  : ""}
              </span>
            ))}
          {data.items.length > MAX_SHOWN && (
            <Button
              variant="link"
              className="p-0 ms-auto fs-13"
              onClick={() => setShowAll((v) => !v)}
              data-testid="pending-show-all"
            >
              {showAll ? "Show less" : `Show all ${data.items.length}`}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default InwardPendingActions;
